import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AuthContext from './AuthContext';

const LicenseContext = createContext();

export const useLicense = () => {
  const context = useContext(LicenseContext);
  if (!context) {
    throw new Error('useLicense must be used within a LicenseProvider');
  }
  return context;
};

export const LicenseProvider = ({ children }) => {
  const authContext = useContext(AuthContext);
  const token = authContext?.token;
  const [licenseStatus, setLicenseStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastChecked, setLastChecked] = useState(null);
  const [error, setError] = useState(null);

  const fetchLicenseStatus = async (forceRefresh = false) => {
    try {
      // Don't fetch if we checked recently and not forcing refresh
      if (!forceRefresh && lastChecked && Date.now() - lastChecked < 5 * 60 * 1000) {
        return licenseStatus;
      }

      setIsLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        // No token, can't check license
        setLicenseStatus(null);
        setIsLoading(false);
        return null;
      }

      const response = await axios.get('/api/license/validation-info', {
        headers: { 'x-auth-token': token }
      });

      setLicenseStatus(response.data);
      setLastChecked(Date.now());
      setError(null);
      return response.data;
    } catch (err) {
      console.error('Error fetching license status:', err);
      setError(err.response?.data?.message || 'Failed to check license status');
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const refreshLicenseStatus = () => {
    return fetchLicenseStatus(true);
  };

  const isReadOnlyMode = () => {
    return licenseStatus?.isReadOnlyMode || false;
  };

  const isLicenseValid = () => {
    return licenseStatus?.isValid || false;
  };

  const getDaysRemaining = () => {
    return licenseStatus?.daysRemaining || 0;
  };

  const getLicenseType = () => {
    return licenseStatus?.licenseType || null;
  };

  const shouldShowWarning = () => {
    return licenseStatus?.warningNeeded || false;
  };

  const getExpirationDate = () => {
    return licenseStatus?.expirationDate || null;
  };

  // Check license status on mount and when token changes
  useEffect(() => {
    if (token) {
      fetchLicenseStatus(true);
    } else {
      setLicenseStatus(null);
      setLastChecked(null);
      setIsLoading(false);
    }
  }, [token]);

  // Set up periodic license checking (every 30 minutes), stops when logged out
  useEffect(() => {
    if (!token) return;

    const interval = setInterval(() => {
      fetchLicenseStatus();
    }, 30 * 60 * 1000); // 30 minutes

    return () => clearInterval(interval);
  }, [token]);

  const value = {
    licenseStatus,
    isLoading,
    error,
    fetchLicenseStatus,
    refreshLicenseStatus,
    isReadOnlyMode,
    isLicenseValid,
    getDaysRemaining,
    getLicenseType,
    shouldShowWarning,
    getExpirationDate
  };

  return (
    <LicenseContext.Provider value={value}>
      {children}
    </LicenseContext.Provider>
  );
};

export default LicenseContext;
